import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import {
  defaultRightToWorkStatus,
  paymentModels,
  RTW_EXPIRY_WARNING_DAYS,
} from '@nxsys/constants/AppConstants';
import { useGetRTWList } from '@nxsys/hooks/api/holidayPay';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetRTWListParams } from '@nxsys/store/slices/report/types';
import { Icons } from '@nxsys/types/icons';
import { formatFullName, getTaxWeekTaxYear } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { ExportToCsv } from 'export-to-csv';
import { debounce, find } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RTWFilterModal, { RTWFilterValues } from '../components/RTWFilterModal';
import { getRTWColumns } from './columns';

const RTWReport: React.FC = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();

  const dispatch = useDispatch();
  const { isBureau } = useSelector((state: AppState) => state.appStore);
  const { rtwList } = useSelector((state: AppState) => state.reportStore);
  const { list, pagination, loading, queryParams } = rtwList;
  const currentTaxYear = getTaxWeekTaxYear().taxYear;

  const [selectedRows, setSelectedRows] = useState<IRTW[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  const { loading: loadingRTWList, run: getRTWList } = useGetRTWList();

  const handleGoBack = useCallback(() => navigate('/reports'), [navigate]);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix={messages['common.rightToWorkExpiry']}
          separator=""
          title=""
        />
      </div>
    );
  }, [messages, handleGoBack]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-x-4">
        <AppButton
          type="primary"
          name={messages['common.export']}
          leftIcon={<Icons.IconArrowExport />}
          variant="outlined"
          onClick={handleExportSelected}
        />
        <AppButton
          type="primary"
          name={messages['common.exportAll']}
          leftIcon={<Icons.IconArrowExport color="#fff" />}
          onClick={handleExportAll}
        />
      </div>
    );
  };

  const handleFetchData = useCallback(
    (params: GetRTWListParams) => {
      const newParams = {
        ...params,
        noOfDays: RTW_EXPIRY_WARNING_DAYS,
      };

      dispatch(reportActions.getRTWList(newParams));
    },
    [dispatch],
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: currentTaxYear,
    });
  }, [currentTaxYear]);

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: RTWFilterValues) => {
    const params: GetRTWListParams = {
      ...queryParams,
      companyIds: values.companies?.map((c) => c.value),
      agencyIds: values.agencies?.map((a) => a.value),
      taxYear: values.taxYear,
    };
    handleFetchData(params);
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleExport = (data: IRTW[]) => {
    const dataExport = data.map((item) => {
      const {
        agencyRef,
        systemId,
        id,
        primaryAgency,
        secondaryAgency,
        companyPayment,
        setting,
        rightToWorkStatus,
        documentExpiryDate,
        shareCodeVerified,
      } = item;
      return {
        id,
        agencyRef: `${primaryAgency?.agencyPrefix || '___'}/${
          agencyRef || systemId || ''
        }`,
        paymentCompany: companyPayment?.companyName || '',

        name: formatFullName(item),
        paymentModel: setting?.paymentModel
          ? paymentModels[setting?.paymentModel as keyof typeof paymentModels]
          : '',
        primaryAgencyName: primaryAgency?.name || '',
        secondaryAgency: secondaryAgency?.name || '',
        rightToWorkStatus:
          find(defaultRightToWorkStatus, (e) => e.value === rightToWorkStatus)
            ?.label || '',

        documentExpiryDate: documentExpiryDate
          ? moment(documentExpiryDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        shareCodeVerified: shareCodeVerified || '',
      };
    });

    const headers = [
      'Employee ID',
      'Agency Reference',
      'Payment Company',
      'Name',
      'Payment Model',
      'Primary Agency',
      'Secondary Agency',
      'Right to Work Status',
      'Document Expire Date',
      'Share Code / EC Check Code',
    ];

    const csvExporter = new ExportToCsv({
      filename: `Right-to-work-expiry-export-${moment(new Date())
        .tz('Europe/London')
        .format('DD/MM/YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    try {
      csvExporter.generateCsv(dataExport);
    } catch (e) {
      console.error('Export failed:', e);
      console.error(e);
    }
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  const handleExportAll = () => {
    if (!pagination.totalDocs) {
      showMessage({
        type: 'warning',
        content: messages['common.noDataToExport'],
      });
      return;
    }
    getRTWList({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
      noOfDays: RTW_EXPIRY_WARNING_DAYS,
    }).then((res) => {
      if (!res?.data?.length) {
        showMessage({
          type: 'warning',
          content: messages['common.noDataToExport'],
        });
        return;
      }
      handleExport(res.data);
    });
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.companyIds ||
    !!queryParams?.agencyIds;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={loadingRTWList}
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4">
              <AppInputSearch onChange={handleSearch} />
              <AppMenuFilter
                options={[]}
                onChange={() => {}}
                onClick={() => setIsOpenFilterModal(true)}
              />
            </div>
            <div className="flex flex-wrap gap-4 items-end">
              <TaxYearSelect
                initValue={currentTaxYear}
                onFilter={(value) =>
                  handleFetchData({ ...queryParams, taxYear: value.taxYear })
                }
              />
            </div>
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 2700, y: 700 }}
            columns={getRTWColumns({
              messages,
              isBureau,
            })}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            onChangeTable={onChangeTable}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
      <RTWFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
      />
    </AppsContainer>
  );
};

export default RTWReport;
